// Lib
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Button as RomaButton, Toggle } from '@milanote/roma';
import { usePlaySound } from '../../../../../sounds/usePlaySound';
import { SoundId } from '../../../../../sounds/soundConstants';
import { EditorToolbar } from '@milanote/roma/application';

// Components
import CancelDiscardButton from './CancelDiscardButton';

// Hooks
import { useTranslator } from '../../../../../components/i18n/useTranslator';
import { useImageModalV2BackgroundRemoval } from '../content/hooks/useImageModalV2BackgroundRemoval';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { hasBackgroundRemovalCache } from '../../backgroundRemoval/imageModalBackgroundRemovalUtils';

// Types
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import type { BackgroundRemovalProgress } from '../../backgroundRemoval/imageModalBackgroundRemovalProgressTypes';
import type { ImageModalV2ErrorToastState } from './imageModalV2ErrorToastTypes';
import type { UserFeatureUsageSummary } from '../../../../../../common/users/userFeatureUsageTypes';

const TOGGLE_ANIMATION_FALLBACK_MS = 210;
const TOGGLE_COMPLETE_HOLD_MS = 100;

type ImageModalV2BackgroundToolbarProps = {
    element: ImMNElement;
    cropSourceImage: MNImageContent;
    largestImageUrl?: string;
    imageType: string;
    progress: BackgroundRemovalProgress | null;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    onUsageChange: (usage: UserFeatureUsageSummary | null) => void;
    ignoreActions: boolean;
    stagedActive: boolean;
    storedActive: boolean;
    onChangeStagedActive: (active: boolean) => void;
    onErrorToastChange: (toast: ImageModalV2ErrorToastState | null) => void;
    onCancelClick: () => void;
    onSaveClick: () => void;
};

const ImageModalV2BackgroundToolbar: React.FC<ImageModalV2BackgroundToolbarProps> = ({
    element,
    cropSourceImage,
    largestImageUrl,
    imageType,
    progress,
    setProgress,
    onUsageChange,
    ignoreActions,
    stagedActive,
    storedActive,
    onChangeStagedActive,
    onErrorToastChange,
    onCancelClick,
    onSaveClick,
}) => {
    const translate = useTranslator('popup', 'imageModal.editor');
    const imageModalTranslate = useTranslator('popup', 'imageModal');
    const elementId = getElementId(element);
    const hasStagedChanges = stagedActive !== storedActive;
    const canShowSave = hasBackgroundRemovalCache(element);
    const toggleWrapRef = useRef<HTMLDivElement>(null);

    const playSound = usePlaySound();
    const { onToggle, onToggleAnimationComplete, isToggleSelected, isToggleAnimating, error, clearError } =
        useImageModalV2BackgroundRemoval({
            element,
            elementId,
            cropSourceImage,
            largestImageUrl,
            imageType,
            setProgress,
            onUsageChange,
            stagedActive,
            onChangeStagedActive,
        });

    useEffect(() => {
        onErrorToastChange(error ? { ...error, onDismiss: clearError } : null);
    }, [clearError, error, onErrorToastChange]);
    useEffect(() => () => onErrorToastChange(null), [onErrorToastChange]);

    useLayoutEffect(() => {
        if (!isToggleAnimating) return;
        const root = toggleWrapRef.current;
        if (!root) return;

        let finished = false;
        let holdTimeout = 0;
        const finish = () => {
            if (finished) return;
            finished = true;
            holdTimeout = window.setTimeout(onToggleAnimationComplete, TOGGLE_COMPLETE_HOLD_MS);
        };
        const handleTransitionEnd = (event: TransitionEvent) => {
            if (!(event.target instanceof Element)) return;
            if (!event.target.closest('.RomaToggleBase')) return;
            if (event.propertyName !== 'transform') return;
            finish();
        };

        root.addEventListener('transitionend', handleTransitionEnd);
        const fallback = window.setTimeout(finish, TOGGLE_ANIMATION_FALLBACK_MS);
        return () => {
            root.removeEventListener('transitionend', handleTransitionEnd);
            window.clearTimeout(fallback);
            window.clearTimeout(holdTimeout);
        };
    }, [isToggleAnimating, onToggleAnimationComplete]);

    const blockActions = ignoreActions || !!progress || isToggleAnimating;

    return (
        <EditorToolbar.Shell>
            <EditorToolbar.List>
                {largestImageUrl && (
                    <div
                        ref={toggleWrapRef}
                        className={
                            isToggleAnimating
                                ? 'pointer-events-none flex items-center px-2.5 py-1.5'
                                : 'flex items-center px-2.5 py-1.5'
                        }
                    >
                        <Toggle
                            size="sm"
                            toggleSize="xs"
                            align="left"
                            className="!items-center [&_.RomaLabelText]:text-sm [&_.RomaLabelText]:text-secondary"
                            label={imageModalTranslate('removeBackground')}
                            isSelected={isToggleSelected}
                            onChange={() => {
                                if (blockActions) return;
                                playSound(isToggleSelected ? SoundId.SwitchToggleOff : SoundId.SwitchToggleOn);
                                onToggle();
                            }}
                        />
                    </div>
                )}

                <EditorToolbar.Divider type="spacer" />

                <div className="flex shrink-0 items-center gap-sm">
                    <CancelDiscardButton showDiscard={hasStagedChanges} onClick={onCancelClick} />

                    {canShowSave && (
                        <RomaButton
                            isDisabled={!hasStagedChanges}
                            onClick={() => {
                                if (blockActions) return;
                                onSaveClick();
                            }}
                        >
                            {translate('save')}
                        </RomaButton>
                    )}
                </div>
            </EditorToolbar.List>
        </EditorToolbar.Shell>
    );
};

export default ImageModalV2BackgroundToolbar;
